import { motion } from "framer-motion";
import { useEffect, useState, useMemo } from "react";
import { Phone, ArrowRight, ShieldCheck, Clock, Truck, Users } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const BADGE_ICONS = [Clock, Users, ShieldCheck, Truck];

function Streaks({ count = 24 }) {
  const streaks = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        id: i,
        top: Math.random() * 100,
        width: 80 + Math.random() * 260,
        duration: 1.8 + Math.random() * 2.6,
        delay: Math.random() * 4,
        opacity: 0.3 + Math.random() * 0.6,
      })),
    [count]
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {streaks.map((s) => (
        <span
          key={s.id}
          className="light-streak"
          style={{
            top: `${s.top}%`,
            width: `${s.width}px`,
            animationDuration: `${s.duration}s`,
            animationDelay: `${s.delay}s`,
            opacity: s.opacity,
          }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  const { t, lang } = useLanguage();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(t);
  }, []);

  const goldSet = new Set(t.hero.goldWords);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src="/hero/hero-blue-sky.jpg"
          alt="Happy trucker with his semi truck under a bright blue sky"
          className="w-full h-full object-cover object-center"
          loading="eager"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink/75 via-ink/20 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/35 via-transparent to-ink" />
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 75% 15%, rgba(255,215,0,0.25), transparent 45%), radial-gradient(ellipse at 10% 80%, rgba(13,13,26,0.5), transparent 60%)",
        }}
      />
      <Streaks count={14} />
      <div className="absolute bottom-0 left-0 right-0 h-20 opacity-40">
        <div className="road-lines h-[2px] mt-10" />
      </div>
      <div
        className="absolute inset-0 mix-blend-overlay opacity-15 pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='200' height='200' filter='url(%23n)' opacity='0.5'/></svg>\")",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 bg-gold/5 text-gold text-xs tracking-[0.3em] uppercase font-semibold mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
          {t.hero.badge}
        </motion.div>

        <h1
          data-testid="hero-headline"
          aria-label={t.hero.ariaHeadline}
          key={lang}
          className="font-heading text-white leading-[0.95] text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-full break-words drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]"
        >
          {t.hero.headlineWords.map((word, i) => (
            <motion.span
              key={`${word}-${i}`}
              initial={{ opacity: 0, y: 40 }}
              animate={ready ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.15 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`inline-block mr-3 ${goldSet.has(word) ? "text-gold" : ""}`}
            >
              {word}
            </motion.span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.1 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.25 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#quote"
            data-testid="primary-cta"
            className="group inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest px-8 py-4 rounded hover:bg-gold-hover transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(255,215,0,0.5)]"
          >
            {t.hero.ctaPrimary}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="tel:7865438906"
            data-testid="secondary-cta"
            className="group inline-flex items-center justify-center gap-2 border-2 border-white/80 text-white font-bold uppercase tracking-widest px-8 py-4 rounded hover:bg-white hover:text-ink transition-all duration-300"
          >
            <Phone size={18} />
            {t.hero.ctaSecondary}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={ready ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 1.5 }}
          className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl"
        >
          {t.hero.trustBadges.map((label, i) => {
            const Icon = BADGE_ICONS[i] || ShieldCheck;
            return (
              <div
                key={label}
                className="flex items-center gap-3 px-4 py-3 rounded border border-ink-line bg-ink-soft/50 backdrop-blur"
              >
                <Icon size={18} className="text-gold shrink-0" />
                <span className="text-xs md:text-sm text-white/80 font-semibold">{label}</span>
              </div>
            );
          })}
        </motion.div>
      </div>

      <a
        href="#stats"
        aria-label={t.hero.scroll}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/60 hover:text-gold transition-colors"
      >
        <div className="bounce-down flex flex-col items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
          <div className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
        </div>
      </a>
    </section>
  );
}
